import React, { useEffect, useMemo, useState } from 'react';

import energyIcon from 'assets/pics/energy_icon.svg';
import gasIcon from 'assets/pics/gas_icon.svg';
import PropTypes from 'prop-types';

import { CustomModal } from 'components/CustomModal';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { SectionSubtitle } from 'components/FormHelper/SectionSubtitle';
import { LabelCheckbox } from 'components/LabelCheckbox/LabelCheckbox';

import { BRANCH_TYPE } from 'global/constants';

import './Popup.sass';
import './ResetExpertBranchPopup.sass';

const calcPreselectEnergy = ({
  selectedBranch,
  isEnergyExtensionUpdated,
  isGasExtensionUpdated,
}) => {
  if (selectedBranch === BRANCH_TYPE.ENERGY) {
    return isEnergyExtensionUpdated;
  }

  return isEnergyExtensionUpdated && !isGasExtensionUpdated;
};

const calcPreselectGas = ({ selectedBranch, isEnergyExtensionUpdated, isGasExtensionUpdated }) => {
  if (selectedBranch === BRANCH_TYPE.GAS) {
    return isGasExtensionUpdated;
  }

  return isGasExtensionUpdated && !isEnergyExtensionUpdated;
};

export const ResetExpertBranchPopup = ({
  mainFormik,
  oldData,
  selectedRow,
  selectedBranch,
  popupOpen,
  setPopupOpen,
  onResetBranches,
}) => {
  const expert = mainFormik.values.experts?.[selectedRow] ?? null;
  const oldExpert = useMemo(
    () => oldData.find((oldExpertData) => oldExpertData.id === expert?.id) ?? null,
    [oldData, expert?.id]
  );
  const isEnergyExtensionUpdated = Boolean(
    expert &&
      oldExpert &&
      (expert.expertEnergyValidityDate !== oldExpert.expertEnergyValidityDate ||
        expert.isExpertExtendedEnergy !== oldExpert.isExpertExtendedEnergy)
  );
  const isGasExtensionUpdated = Boolean(
    expert &&
      oldExpert &&
      (expert.expertGasValidityDate !== oldExpert.expertGasValidityDate ||
        expert.isExpertExtendedGas !== oldExpert.isExpertExtendedGas)
  );

  const [selectedEnergy, setSelectedEnergy] = useState(false);
  const [selectedGas, setSelectedGas] = useState(false);

  useEffect(() => {
    if (!popupOpen) {
      return;
    }

    const preselectEnergy = calcPreselectEnergy({
      selectedBranch,
      isEnergyExtensionUpdated,
      isGasExtensionUpdated,
    });
    const preselectGas = calcPreselectGas({
      selectedBranch,
      isEnergyExtensionUpdated,
      isGasExtensionUpdated,
    });

    setSelectedEnergy(preselectEnergy);
    setSelectedGas(preselectGas);
  }, [popupOpen, selectedBranch, isEnergyExtensionUpdated, isGasExtensionUpdated]);

  const onCancel = () => {
    setPopupOpen(false);
  };

  const onBranchSelectChange = (event) => {
    const { name, value } = event.target;
    if (name === 'resetEnergy') {
      setSelectedEnergy(value);
    }
    if (name === 'resetGas') {
      setSelectedGas(value);
    }
  };

  const onSubmit = () => {
    if (!expert) {
      return;
    }

    const branchesToReset = [
      ...(selectedEnergy && isEnergyExtensionUpdated ? [BRANCH_TYPE.ENERGY] : []),
      ...(selectedGas && isGasExtensionUpdated ? [BRANCH_TYPE.GAS] : []),
    ];

    if (!branchesToReset.length) {
      return;
    }

    onResetBranches(expert.id, branchesToReset);
    setPopupOpen(false);
  };

  const hasNoSelectedBranch = !selectedEnergy && !selectedGas;

  const modalContent = () => {
    if (!expert) {
      return <div>Kein Eintrag ausgewählt</div>;
    }

    return (
      <div className="popup-content reset-expert-branch-popup">
        <div className="reset-expert-branch-popup__subtitle">
          Für welche Sparte soll die Verlängerung für die Fachkraft{' '}
          <span className="edit-popup-expert-name">{expert.completeName}</span> zurückgesetzt
          werden?
        </div>
        <div className="popup-content__popup-field reset-expert-branch-popup__branches">
          <SectionSubtitle>Für die Sparte:</SectionSubtitle>
          <div className="flex-row">
            {isEnergyExtensionUpdated && (
              <LabelCheckbox
                label="Strom"
                id="resetEnergy"
                name="resetEnergy"
                icon={energyIcon}
                value={selectedEnergy}
                onChange={onBranchSelectChange}
              />
            )}
            {isGasExtensionUpdated && (
              <LabelCheckbox
                label="Gas"
                id="resetGas"
                name="resetGas"
                icon={gasIcon}
                value={selectedGas}
                onChange={onBranchSelectChange}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Zurücksetzen"
      content={modalContent()}
      closeCancelEventModal={onCancel}
      ctaButtons={
        <FormActionButtons
          hasErrors={hasNoSelectedBranch}
          titleLeft="Abbrechen"
          titleRight="Zurücksetzen"
          onClickLeft={onCancel}
          onClickRight={onSubmit}
        />
      }
    />
  );
};

ResetExpertBranchPopup.propTypes = {
  mainFormik: PropTypes.object.isRequired,
  oldData: PropTypes.array.isRequired,
  selectedRow: PropTypes.number,
  selectedBranch: PropTypes.oneOf([BRANCH_TYPE.ENERGY, BRANCH_TYPE.GAS]),
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  onResetBranches: PropTypes.func.isRequired,
};

ResetExpertBranchPopup.defaultProps = {
  popupOpen: false,
  selectedRow: null,
  selectedBranch: null,
  setPopupOpen: () => {},
};
